import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { Sparkle, ChevronDown, Copy } from "lucide-react";
import { getPromptsByWorkpill, Prompt } from "../../services/promptService";
import { ICON_SIZES } from "@/lib/icon-sizes";
import { cn, copyToClipboard } from "@/lib/utils";

interface PromptsDropdownProps {
  workpillId?: string;
}

const PromptsDropdown: React.FC<PromptsDropdownProps> = ({ workpillId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const loadingRef = useRef(false);

  const loadPrompts = async () => {
    if (!workpillId || loadingRef.current) {
      return;
    }

    loadingRef.current = true;
    setIsLoading(true);
    setError(null);
    try {
      const data = await getPromptsByWorkpill(workpillId);
      setPrompts(data);
    } catch (err) {
      console.error('Error loading prompts:', err);
      setError('Error loading prompts');
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  };

  // Cargar prompts cuando se abre el desplegable y hay workpillId
  useEffect(() => {
    if (isOpen && workpillId) {
      loadPrompts();
    }
  }, [isOpen, workpillId]);

  // Reset prompts cuando cambia el workpillId
  useEffect(() => {
    setPrompts([]);
    setError(null);
  }, [workpillId]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleCopyPrompt = async (prompt: Prompt) => {
    const success = await copyToClipboard(prompt.prompt);
    if (success) {
      setCopiedId(prompt.id);
      // Reset después de 2 segundos
      setTimeout(() => {
        setCopiedId(null);
      }, 2000);
    }
  };

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isOpen]);

  if (!workpillId) {
    return null; // No mostrar si no hay workpillId
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleToggle}
        data-prompts-button
        className="p-0 border-none bg-transparent cursor-pointer rounded-md flex items-center justify-center text-muted-foreground gap-1 transition-all h-7 w-7 relative hover:bg-primary/5 hover:text-primary"
        title="Prompts"
      >
        <Sparkle size={ICON_SIZES.sm} />
        {prompts.length > 0 && (
          <span className="absolute top-0.5 right-0.5 bg-primary text-primary-foreground text-[7px] font-semibold px-1 rounded min-w-[10px] h-[10px] flex items-center justify-center leading-none border border-white">
            {prompts.length > 9 ? '9+' : prompts.length}
          </span>
        )}
      </button>

      {isOpen && (
        <div 
          ref={dropdownRef}
          data-prompts-dropdown
          className="absolute top-full right-0 w-[300px] bg-card border border-border rounded-lg shadow-lg z-[1000] max-h-[380px] overflow-y-auto mt-1.5"
        >
          <div className="py-2.5 px-3 border-b border-border bg-muted flex justify-between items-center rounded-t-lg sticky top-0 z-10">
            <span className="text-sm font-semibold text-foreground">
              Prompts ({prompts.length})
            </span>
            <button 
              onClick={() => setIsOpen(false)}
              className="bg-transparent border-none cursor-pointer text-muted-foreground text-sm p-0.5 rounded hover:bg-primary/5 hover:text-primary"
            >
              ×
            </button>
          </div>

          {isLoading ? (
            <div className="p-6 text-center text-muted-foreground text-sm">
              Loading prompts...
            </div>
          ) : error ? (
            <div className="p-4 text-destructive text-center text-sm">
              {error}
            </div>
          ) : prompts.length === 0 ? (
            <div className="p-6 text-muted-foreground text-center text-sm">
              No prompts available
            </div>
          ) : (
            <div className="py-1">
              {prompts.map((prompt, index) => {
                const isCopied = copiedId === prompt.id;
                return (
                  <div
                    key={prompt.id}
                    className={cn(
                      "p-3 transition-colors hover:bg-primary/5",
                      index < prompts.length - 1 && "border-b border-muted"
                    )}
                  >
                    <div className="flex justify-between items-start mb-2 gap-2">
                      <h4 className="text-sm font-semibold text-foreground m-0 flex-1 leading-snug">
                        {prompt.title}
                      </h4>
                      <button
                        onClick={() => handleCopyPrompt(prompt)}
                        className={cn(
                          "py-1 px-2 border-none rounded-md cursor-pointer flex items-center gap-1 text-xs font-medium transition-all flex-shrink-0",
                          isCopied 
                            ? "bg-primary text-primary-foreground" 
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        )}
                        title={isCopied ? 'Copied' : 'Copy prompt'}
                      >
                        <Copy size={ICON_SIZES.xs} />
                        {isCopied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground m-0 leading-relaxed line-clamp-2">
                      {prompt.prompt}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PromptsDropdown;






